/**
 * Shown by RequireAuth when /me is refused by the identity/access model
 * (an account denial — disabled, not-provisioned, org-disabled, or email
 * unverified), or when the response failed schema validation (a
 * frontend/backend version skew — `CONTRACT_VIOLATION`, not an access
 * decision). Neither can be cleared by re-authenticating, so we show an
 * explanatory screen with a deliberate sign-out (which ends the Cognito Hosted
 * UI session) rather than bouncing to /login, where the Cognito cookie would
 * silently re-login into the same failure. Account-denial body copy comes
 * from `ACCOUNT_DENIAL_MESSAGES` in `@compliance/domain` — the same map
 * `AuthProvider` tests membership of, so the copy and the decision to show it
 * cannot drift apart the way they had.
 */

import { ACCOUNT_DENIAL_MESSAGES } from "@compliance/domain";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";

/**
 * Titles are UI framing only — not part of the backend error taxonomy.
 *
 * Every key of `ACCOUNT_DENIAL_MESSAGES` needs one, and the test beside this file
 * enforces it. Missing a title does not fail loudly: the body stays specific and only
 * the heading falls back, so the screen reads "Account unavailable" over copy naming
 * the exact problem — the generic half undercutting the actionable half, on the screen
 * whose entire job is to be actionable. `CONTRACT_VIOLATION` is the one title with no
 * denial-map entry, being a version skew rather than an access decision.
 */
const TITLE: Record<string, string> = {
  ACCOUNT_DISABLED: "Account disabled",
  ACCESS_DISABLED: "Access revoked",
  ACCOUNT_NOT_PROVISIONED: "Account pending approval",
  IDENTITY_NOT_PROVISIONED: "Account not set up",
  ORGANIZATION_DISABLED: "Organization suspended",
  EMAIL_UNVERIFIED: "Email not verified",
  TENANT_NOT_REGISTERED: "Microsoft sign-in not enabled",
  CONTRACT_VIOLATION: "Update required",
};

/** No backend equivalent — a frontend/backend version skew, not an error code. */
const CONTRACT_VIOLATION_BODY =
  "This app can't verify your account right now — it may be out of date. Try reloading the page; if that doesn't help, sign out and back in, or contact support.";

const FALLBACK = {
  title: "Account unavailable",
  body: "Your account can't access the compliance workspace right now. Contact your administrator.",
};

export function AccountInactive({ code }: { code: string }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const title = TITLE[code] ?? FALLBACK.title;
  const body =
    ACCOUNT_DENIAL_MESSAGES[code as keyof typeof ACCOUNT_DENIAL_MESSAGES] ??
    (code === "CONTRACT_VIOLATION" ? CONTRACT_VIOLATION_BODY : FALLBACK.body);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      // A rejected signOut means the API could not end the session and it is
      // still live — so do not navigate as though it ended. The provider sets
      // its error, but nothing in the protected UI renders that (only
      // LoginPage does, and we deliberately do not go there), so the user would
      // otherwise watch the menu close and conclude they were signed out.
      notifications.show({
        color: "red",
        title: "Sign-out failed",
        message:
          err instanceof Error
            ? err.message
            : "You are still signed in. Check your connection and try again.",
        autoClose: false,
      });
      return;
    }
    // A configured Hosted UI redirects and never reaches here; otherwise this
    // local navigation takes the user to /login.
    navigate("/login", { replace: true });
  };

  return (
    <Center h="100vh">
      <Stack align="center" gap="md" maw={440} px="md">
        <Title order={2} ta="center">
          {title}
        </Title>
        <Text c="dimmed" ta="center">
          {body}
        </Text>
        <Button variant="light" onClick={handleSignOut}>
          Sign out
        </Button>
      </Stack>
    </Center>
  );
}
